import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import { ArrowUpRight, Check, CheckCheck, Heart, MessageCircle, PhoneMissed, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const scenarios = ['good', 'followup', 'missed'] as const;
type Scenario = typeof scenarios[number];

export function SampleReport() {
    const { t } = useTranslation();
    const [scenario, setScenario] = useState<Scenario>('good');
    const isMissed = scenario === 'missed';
    const key = `sampleReport.scenarios.${scenario}`;
    const benefits = [Heart, MessageCircle, TrendingUp];

    const handleTabKey = (event: KeyboardEvent<HTMLButtonElement>) => {
        let index = scenarios.indexOf(scenario);
        if (event.key === 'ArrowRight') index = (index + 1) % scenarios.length;
        else if (event.key === 'ArrowLeft') index = (index + scenarios.length - 1) % scenarios.length;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = scenarios.length - 1;
        else return;
        event.preventDefault();
        setScenario(scenarios[index]);
        document.getElementById(`report-tab-${scenarios[index]}`)?.focus();
    };

    return (
        <section id="sample-report" className="landing-section landing-report-section" aria-labelledby="sample-title">
            <div className="landing-container landing-report-grid">
                <div>
                    <div className="landing-section-heading">
                        <p className="landing-eyebrow">{t('sampleReport.badge')}</p>
                        <h2 id="sample-title">{t('sampleReport.title')}</h2>
                        <p>{t('sampleReport.subtitle')}</p>
                    </div>
                    <div className="landing-report-benefits">
                        {benefits.map((Icon, index) => <div key={index}>
                            <Icon size={21} strokeWidth={1.6} aria-hidden="true" />
                            <div><h3>{t(`sampleReport.benefits.${index}.title`)}</h3><p>{t(`sampleReport.benefits.${index}.desc`)}</p></div>
                        </div>)}
                    </div>
                </div>
                <div className="landing-report-demo">
                    <div className="landing-report-tabs" role="tablist" aria-label={t('sampleReport.tabLabel')}>
                        {scenarios.map(value => <button key={value} id={`report-tab-${value}`} type="button" role="tab" aria-selected={scenario === value} aria-controls="report-panel" tabIndex={scenario === value ? 0 : -1} onClick={() => setScenario(value)} onKeyDown={handleTabKey}>{t(`sampleReport.scenarios.${value}.tab`)}</button>)}
                    </div>
                    <div className={`landing-sample-card landing-sample-${scenario}`} id="report-panel" role="tabpanel" aria-labelledby={`report-tab-${scenario}`} tabIndex={0}>
                        <div className="landing-sample-header">
                            <span className="landing-sample-avatar" aria-hidden="true">{t('sampleReport.initial')}</span>
                            <div><h3>{t('sampleReport.parent')}</h3><p>{t('sampleReport.date')}</p></div>
                            <span className="landing-example-tag">{t('sampleReport.example')}</span>
                        </div>
                        <div className="landing-sample-status">{isMissed ? <PhoneMissed size={15} aria-hidden="true" /> : <CheckCheck size={16} aria-hidden="true" />}{t(`${key}.status`)}</div>
                        <h4>{t(`${key}.title`)}</h4>
                        <p className="landing-sample-summary">{t(`${key}.summary`)}</p>
                        <div className="landing-sample-details">
                            <p className="landing-mini-label">{t(`${key}.detailLabel`)}</p>
                            {[0, 1].map(index => <p key={index}>{scenario === 'good' ? <Check size={15} aria-hidden="true" /> : <ArrowUpRight size={15} aria-hidden="true" />}<span>{t(`${key}.details.${index}`)}</span></p>)}
                        </div>
                        <div className="landing-sample-footer"><span>{t('sampleReport.source')}</span><span>{t(`${key}.footer`)}</span></div>
                    </div>
                    <p className="landing-sample-caption">{t('sampleReport.caption')}</p>
                </div>
            </div>
        </section>
    );
}
